import { Dropdown } from "primereact/dropdown";
import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./KrogerReport.module.scss";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import * as constants from "../../constants/constants";
import {
  createStoreSummaryHeaderGroup,
  storeSummaryColumns
} from "../../constants/table-columns/krogerReportTableColumns";
import Spinner from "../../components/spinner/spinner";
import {
  editStoreSummaryCreditReason,
  editStoreSummaryDenialReason,
  getStoreSummary
} from "../../services/KrogerReportService";
import { formatInTimeZone } from "date-fns-tz";
import { Toast } from "primereact/toast";

const StoreSummary = ({ dateRange }) => {
  const toast = useRef();
  const [selectedRegion, setSelectedRegion] = useState("All");
  const [selectedDivision, setSelectedDivision] = useState("All");
  const [excludedUnits, setExcludedUnits] = useState([]);
  const [storeNumber, setStoreNumber] = useState("");
  const [storeSummaryData, setStoreSummaryData] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [editedReason, setEditedReason] = useState("");
  const [showSpinner, setShowSpinner] = useState(false);

  const BUSINESS_UNITS = [
    "Fresh Kitchen Deli",
    "Fresh Kitchen Produce",
    "Retail Produce"
  ];
  const krogerRegions = ["All", "TFTN", "TFNW", "TFSW", "TFTX"];
  const krogerDivisions = [
    "All",
    "011 Atlanta",
    "014 Cincy / Dayton",
    "016 Columbus",
    "018 Michigan",
    "021 Central",
    "024 Louisville",
    "025 Delta",
    "026 Nashville",
    "029 Mid-Atlantic",
    "034 Houston",
    "035 Dallas",
    "615 Dillons",
    "620 King Soopers",
    "660 Fry's Of Arizona",
    "701 Fred Meyer",
    "705 Quality Food Centers",
    "706 Smiths",
    "708 Food 4 Less"
  ];
  const creditReasons = [
    "All",
    "Vendor Error",
    "Vendor Quality",
    "Logistics Error",
    "Logistics Handling",
    "Logistics Missed Delivery (complete loss)",
    "Logistics Missed Delivery (day late)",
    "Logistics Missed Delivery – Mechanical",
    "Logistics Late Delivery after 1 PM",
    "Kitchen Late Delivery"
  ];
  const denialReasons = [
    "Missing Div and/or Store #",
    "Missing Invoice & Date",
    "Missing Valid UPC",
    "Missing RPC Info on Kroger Form",
    "Kroger Credit Form Not Submitted",
    "Credit Submitted Past Deadline",
    "Credit QTY Below Threshold"
  ];
  const toggleUnit = (unit) => {
    setExcludedUnits((prev) =>
      prev.includes(unit) ? prev.filter((u) => u !== unit) : [...prev, unit]
    );
  };
  const convertToPacific = (date) => {
    if (!date) return null;
    return formatInTimeZone(
      new Date(date),
      "America/Los_Angeles",
      "yyyy-MM-dd'T'HH:mm:ss.SSS"
    );
  };
  const handleGetStoreSummary = async () => {
    try {
      setShowSpinner(true);
      const payload = {
        startDate: convertToPacific(dateRange?.startDate),
        endDate: convertToPacific(dateRange?.endDate),
        regions: selectedRegion === "All" ? [] : [selectedRegion],
        divs: selectedDivision === "All" ? [] : [selectedDivision],
        storeNumber: storeNumber.trim()
      };
      const response = await getStoreSummary(payload);
      setStoreSummaryData(response?.blocks ?? []);
      setShowSpinner(false);
    } catch (error) {
      setShowSpinner(false);
      setStoreSummaryData([]);
    }
  };
  useEffect(() => {
    if (dateRange?.startDate && dateRange?.endDate) {
      handleGetStoreSummary();
    }
  }, [dateRange?.startDate, dateRange?.endDate]);

  const formatCurrency = (value) =>
    Number(value ?? 0).toLocaleString("en-US", {
      style: "currency",
      currency: "USD"
    });

  const visibleColumns = useMemo(() => {
    return storeSummaryColumns?.filter(({ group }) => {
      if (
        excludedUnits.includes("Fresh Kitchen Deli") &&
        group === "kitchenDeli"
      ) {
        return false;
      }
      if (
        excludedUnits.includes("Fresh Kitchen Produce") &&
        group === "kitchenProduce"
      ) {
        return false;
      }
      if (
        excludedUnits.includes("Retail Produce") &&
        group === "kitchenRetail"
      ) {
        return false;
      }
      return true;
    });
  }, [excludedUnits]);

  const headerGroup = useMemo(
    () => createStoreSummaryHeaderGroup(visibleColumns),
    [visibleColumns]
  );

  const handleEditClick = (row, group, field) => {
    setEditingCell({
      rowId: row.rowId,
      group,
      field
    });

    setEditedReason(row[group]?.[field] ?? "");
  };

  const handleSaveReason = async (row, group, field) => {
    if (!row?.invoiceId || !editedReason) return;
    try {
      setShowSpinner(true);
      const payload = {
        invoiceId: row?.invoiceId,
        reason: editedReason
      };
      let response;
      if (field === "creditReason") {
        response = await editStoreSummaryCreditReason(payload);
      } else if (field === "denialReason") {
        response = await editStoreSummaryDenialReason(payload);
      }
      if (response == null) return;
      handleGetStoreSummary();
      toast?.current?.show({
        severity: "success",
        summary: "Success",
        detail: `${
          field === "creditReason" ? "Credit" : "Denial"
        } reason updated successfully`,
        life: 4000
      });
      setShowSpinner(false);
      setEditingCell(null);
      setEditedReason("");
    } catch (error) {
      setShowSpinner(false);
      toast?.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.message,
        life: 4000
      });
    }
  };
  const renderCell = (row, column) => {
    const { field, group } = column;
    if (row.rowType === "weekTotals") {
      if (field === "divName") return row.divName ?? "";
      if (field === "date") return "";
      if (
        [
          "storeNumber",
          "invoiceNumber",
          "invoiceDate",
          "creditReason",
          "denialReason",
          "reversalDate"
        ].includes(field)
      ) {
        return "";
      }
      return group ? formatCurrency(row[group]?.[field]) : "";
    }
    if (field === "divName") return row.divName ?? "";
    if (field === "date") return row.date ?? "";

    if (group) {
      const value = row[group]?.[field];
      if (["creditReason", "denialReason"].includes(field)) {
        const isEditing =
          editingCell?.rowId === row.rowId &&
          editingCell?.group === group &&
          editingCell?.field === field;

        const options =
          field === "creditReason"
            ? creditReasons.filter((reason) => reason !== "All")
            : denialReasons;

        if (isEditing) {
          return (
            <div className="flex align-items-center gap-2">
              <Dropdown
                value={editedReason}
                options={options}
                onChange={(e) => setEditedReason(e.value)}
                placeholder="Select reason"
                className={styles.dropdown}
              />

              <Button
                icon="pi pi-save"
                text
                rounded
                size="small"
                tooltip="Save"
                disabled={!editedReason}
                onClick={() => handleSaveReason(row, group, field)}
              />
              <Button
                icon="pi pi-times"
                text
                rounded
                size="small"
                tooltip="Cancel"
                onClick={() => {
                  setEditingCell(null);
                  setEditedReason("");
                }}
              />
            </div>
          );
        }
        return value ? (
          <div className="flex align-items-center gap-2">
            <span>{value}</span>
            <Button
              icon="pi pi-pencil"
              text
              rounded
              size="small"
              tooltip="Edit"
              onClick={() => handleEditClick(row, group, field)}
            />
          </div>
        ) : (
          ""
        );
      }
      if (
        [
          "storeNumber",
          "invoiceNumber",
          "invoiceDate",
          "reversalDate"
        ].includes(field)
      ) {
        return value ?? "";
      }
      return formatCurrency(value);
    }
    return row[field] ?? "";
  };

  const getStoreRows = (block) => {
    return (block?.weeks ?? []).flatMap((week) => {
      const stores = (week?.rows ?? []).map((item, index) => ({
        rowId: `${block.region}-${block.divName}-${week.weekNo}-${index}`,
        rowType: "store",
        invoiceId: item.invoiceId,
        region: block.region,
        divName: item.divName,
        date: item.date,
        store: item.store,
        weekNo: week.weekNo,
        weekLabel: week.weekLabel,
        kitchenDeli: item.deli ?? {},
        kitchenProduce: item.produce ?? {},
        kitchenRetail: item.retail ?? {}
      }));

      const totalsRow = {
        rowId: `${block.region}-${block.divName}-${week.weekNo}-totals`,
        rowType: "weekTotals",
        region: block.region,
        divName: week.weekLabel,
        date: "",
        kitchenDeli: week?.weekTotals?.deli ?? {},
        kitchenProduce: week?.weekTotals?.produce ?? {},
        kitchenRetail: {
          totalCredit: week?.weekTotals?.retail ?? 0
        }
      };

      return [...stores, totalsRow];
    });
  };
  const blocksToDisplay =
    storeSummaryData?.length > 0
      ? storeSummaryData
      : [{ region: "", divName: "", weeks: [] }];

  return (
    <div>
      <Toast ref={toast} position="center" />
      {showSpinner && <Spinner />}
      <div className="grid">
        <div className="col-12 lg:col-3">
          <span>Region: </span>
          <Dropdown
            options={krogerRegions}
            value={selectedRegion}
            onChange={(e) => setSelectedRegion(e.value)}
            placeholder="Select Region"
            className={styles?.dropdown}
          />
        </div>
        <div className="col-12 lg:col-3">
          <span>Div: </span>
          <Dropdown
            options={krogerDivisions}
            value={selectedDivision}
            onChange={(e) => setSelectedDivision(e.value)}
            placeholder="Select Division"
            className={styles?.dropdown}
          />
        </div>
        <div className="col-12 lg:col-3">
          <span>Store#: </span>
          <InputText
            value={storeNumber}
            onChange={(e) => setStoreNumber(e.target.value)}
          />
        </div>
        <div className="col-12 lg:col-2">
          <Button
            type="submit"
            size="medium"
            className={styles?.primaryBtn}
            onClick={handleGetStoreSummary}
          >
            Search
          </Button>
        </div>
      </div>
      <div className={styles.filterBar}>
        {BUSINESS_UNITS?.map((unit) => {
          const active = !excludedUnits.includes(unit);
          return (
            <button
              key={unit}
              type="button"
              aria-pressed={active}
              className={`${styles.filterChip} ${
                active ? styles.filterChipActive : ""
              }`}
              onClick={() => toggleUnit(unit)}
            >
              <i className={`pi ${active ? "pi-check" : "pi-plus"}`} />

              {unit}
            </button>
          );
        })}

        <div className="ml-auto">
          <Button className={styles.downloadButton}>
            <i className="pi pi-download" />

            <span>Download</span>
          </Button>
        </div>
      </div>
      {blocksToDisplay?.map((block) => {
        const storeRows = getStoreRows(block);
        const hasBlockData = storeSummaryData?.length > 0;
        return (
          <div
            key={`${block?.region}-${block?.divName}`}
            className={styles.card}
          >
            {hasBlockData && (
              <div className={styles.cardHeader}>
                <h2>
                  {block?.region} - {block?.divName}
                </h2>
              </div>
            )}
            <DataTable
              className={styles.regionDataGrid}
              value={storeRows}
              dataKey="rowId"
              scrollable
              scrollHeight="30vw"
              tableStyle={{
                minWidth: "50rem",
                width: "max-content"
              }}
              headerColumnGroup={headerGroup}
              paginator={constants?.paginator}
              rows={100}
              rowClassName={(row) =>
                row?.rowType === "weekTotals" ? styles.totalRow : ""
              }
              rowsPerPageOptions={constants?.rowsPerPageOptions}
              paginatorTemplate={constants?.paginatorTemplate}
              currentPageReportTemplate={constants?.currentPageReportTemplate}
              emptyMessage={constants?.emptyMessage}
            >
              {visibleColumns?.map((column) => (
                <Column
                  key={`${column.group ?? "main"}-${column.field}`}
                  field={column.field}
                  header={column.header}
                  style={{
                    minWidth: column.minWidth,
                    width: column.minWidth
                  }}
                  body={(row) => renderCell(row, column)}
                  alignHeader={column.field === "divName" ? "left" : "center"}
                  bodyClassName={(row) =>
                    `${row?.rowType === "weekTotals" ? "font-bold " : ""}${
                      column.group === "kitchenRetail" ? "retailDataCell" : ""
                    }`
                  }
                />
              ))}
            </DataTable>
          </div>
        );
      })}
    </div>
  );
};

export default StoreSummary;
